import type { Page } from '@inertiajs/core';
import { databaseService } from '@/services/database/DatabaseService';
import { shouldPersistInertiaPageToSqlite } from '@/mobile/native/inertiaPageCachePolicy';

const LAST_URL_KEY = 'last_inertia_url';
const LOGIN_COMPONENT = 'modules/auth/pages/Login';

let sqlitePersistChain: Promise<void> = Promise.resolve();

function enqueueSqlitePagePersist(task: () => Promise<void>): void {
  sqlitePersistChain = sqlitePersistChain
    .then(task)
    .catch((error) => {
      console.warn('[inertia-cache] falha na fila de persistência', error);
    });
}

const memoryPageCache = new Map<
  string,
  { page: Page; inertiaVersion: string | null }
>();

function rememberInMemory(
  key: string,
  page: Page,
  inertiaVersion?: string | null,
): void {
  if (memoryPageCache.size > 48) {
    const first = memoryPageCache.keys().next().value;
    if (first) {
      memoryPageCache.delete(first);
    }
  }

  memoryPageCache.set(key, { page, inertiaVersion: inertiaVersion ?? null });
}

export function normalizeCacheKey(url: string): string {
  try {
    const parsed = new URL(url, 'https://app.local');
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export function isLoginInertiaPage(page: Page): boolean {
  return page.component === LOGIN_COMPONENT;
}

export function cacheKeysForPath(path: string): string[] {
  const keys = new Set<string>();
  const normalized = normalizeCacheKey(path);

  keys.add(normalized);
  keys.add(normalizeCacheKey(path.startsWith('http') ? path : `https://app.local${normalized}`));

  if (path.startsWith('/api/mobile/dashboard') || normalized === '/api/mobile/dashboard') {
    keys.add('/dashboard');
  }

  if (path.startsWith('/api/mobile/settings/')) {
    keys.add(normalized.replace('/api/mobile', ''));
  }

  return [...keys];
}

export async function setLastVisitedPage(url: string): Promise<void> {
  await databaseService.setMeta(LAST_URL_KEY, normalizeCacheKey(url));
}

export async function saveInertiaPageCache(
  url: string,
  page: Page,
  inertiaVersion?: string | null,
  options?: { trackAsLast?: boolean },
): Promise<void> {
  if (isLoginInertiaPage(page)) {
    return;
  }

  const key = normalizeCacheKey(url);
  rememberInMemory(key, page, inertiaVersion);

  if (options?.trackAsLast === true) {
    void setLastVisitedPage(url);
  }

  // Persistência SQLite fora do caminho crítico da navegação (evita jank no APK).
  if (!shouldPersistInertiaPageToSqlite(page)) {
    return;
  }

  const payload = JSON.stringify(page);
  const version = inertiaVersion ?? null;
  const updatedAt = new Date().toISOString();

  void enqueueSqlitePagePersist(async () => {
    await databaseService.runQuery(
      `INSERT INTO inertia_page_cache (cache_key, page_json, inertia_version, updated_at)
       VALUES (?, ?, ?, ?)
       ON CONFLICT(cache_key) DO UPDATE SET
         page_json = excluded.page_json,
         inertia_version = excluded.inertia_version,
         updated_at = excluded.updated_at`,
      [key, payload, version, updatedAt],
    );
  });
}

export async function hasInertiaPageCache(url: string): Promise<boolean> {
  const key = normalizeCacheKey(url);
  const rows = await databaseService.queryRows<{ page_json: string }>(
    `SELECT page_json FROM inertia_page_cache WHERE cache_key = ? LIMIT 1`,
    [key],
  );

  return Boolean(rows[0]?.page_json);
}

export async function loadInertiaPageCache(url: string): Promise<{
  page: Page;
  inertiaVersion: string | null;
} | null> {
  const key = normalizeCacheKey(url);
  const memory = memoryPageCache.get(key);
  if (memory) {
    return memory;
  }

  const rows = await databaseService.queryRows<{
    page_json: string;
    inertia_version: string | null;
  }>(`SELECT page_json, inertia_version FROM inertia_page_cache WHERE cache_key = ? LIMIT 1`, [
    key,
  ]);

  const row = rows[0];
  if (!row?.page_json) {
    return null;
  }

  try {
    const page = JSON.parse(row.page_json) as Page;

    if (isLoginInertiaPage(page)) {
      return null;
    }

    rememberInMemory(key, page, row.inertia_version ?? null);

    return {
      page,
      inertiaVersion: row.inertia_version ?? null,
    };
  } catch {
    return null;
  }
}

export async function loadLastInertiaPageCache(): Promise<{
  page: Page;
  inertiaVersion: string | null;
} | null> {
  const lastKey = await databaseService.getMeta(LAST_URL_KEY);
  if (!lastKey) {
    return null;
  }

  return loadInertiaPageCache(lastKey);
}

export function buildLoginPage(): Page {
  return {
    component: LOGIN_COMPONENT,
    props: {
      locale: 'pt_BR',
      availableLocales: ['pt_BR'],
      canResetPassword: true,
      canRegister: false,
      status: undefined,
    },
    url: '/',
    version: '',
    clearHistory: false,
    encryptHistory: false,
    rememberedState: {},
  } as Page;
}
